import { IField } from '@/document-edit/assembler/fields/types/field';
import { FieldDTOHelper } from '@/api/fields/helpers/field-dto.helper';
import { EMergeType } from '@lib-shared/fields/types/enums/merge-type';
import { EFieldType } from '@lib-shared/fields/types/enums/field-type';
import { EMergeValue } from '@/document-edit/assembler/fields/types/enums/merge-value';
import { IFieldPosition } from '@/document-edit/assembler/fields/types/field-position';
import { IUser } from '@/types/User';
import { MergeTagHelper } from '@/helpers/merge-tag.helper';
import { FieldHelper } from '@/document-edit/assembler/fields/helpers/field.helper';

export function createDefaultField(
  pageId: string,
  documentId: string,
  type: EFieldType,
  pageWidth: number,
  connectFirstRecipient: boolean,
  mergeValue?: EMergeValue,
  sender?: IUser,
  recipientId: string = null,
  top?: number,
  left?: number,
  lastUpdatedField?: IField
): IField {
  let partialField: Partial<IField>;

  switch (type) {
    case EFieldType.Link:
      partialField = getDefaultLinkField();
      break;
    case EFieldType.Initials:
    case EFieldType.Signature:
      partialField = getDefaultSigningField(connectFirstRecipient, recipientId);
      break;
    case EFieldType.Dropdown:
    case EFieldType.Formatted:
    case EFieldType.Text:
    case EFieldType.Check:
      partialField = getDefaultField(recipientId);
      break;
    case EFieldType.Merge:
      partialField = getDefaultMergeField(mergeValue, sender, recipientId);
      partialField.displayProperty = MergeTagHelper.getMergeDisplayProperty(partialField.value);
      break;
  }
  const position: IFieldPosition = getFieldPosition(type, pageWidth, top, left, lastUpdatedField);
  return {
    ...partialField,
    label: '',
    documentId: documentId,
    pageId,
    type,
    isRequired: false,
    position,
    isCollectable: FieldHelper.isCollectable(
      recipientId,
      type,
      partialField.value,
      connectFirstRecipient
    ),
    source: FieldDTOHelper.getFieldSource(
      type,
      partialField.value,
      recipientId,
      null,
      false,
      connectFirstRecipient
    ),
  } as IField;
}

function getFieldPosition(
  type: EFieldType,
  pageWidth: number,
  top: number,
  left: number,
  lastUpdatedField?: IField
): IFieldPosition {
  const isDroppedField: boolean = !!top && !!left;
  if (isDroppedField) {
    return defaultPositionDroppedField(type, left, top, pageWidth);
  }
  if (lastUpdatedField && lastUpdatedField.position) {
    return {
      ...defaultSizes(type),
      left: lastUpdatedField.position.left,
      top: lastUpdatedField.position.top,
    };
  }
  return {
    ...defaultSizes(type),
    left: 100,
    top: 100,
  };
}

function getDefaultLinkField(): Partial<IField> {
  return {
    value: '',
    isEditable: true,
  };
}

function getDefaultField(recipientId?: string): Partial<IField> {
  return {
    value: '',
    isEditable: true,
    recipientId,
  };
}

function getDefaultSigningField(
  connectFirstRecipient: boolean,
  recipientId?: string
): Partial<IField> {
  return {
    value: recipientId || connectFirstRecipient ? 'recipient' : 'sender',
    isEditable: true,
    recipientId,
  };
}

function getDefaultMergeField(
  mergeValue?: EMergeValue,
  sender?: IUser,
  recipientId?: string
): Partial<IField> {
  if (!mergeValue && recipientId) {
    return {
      value: '',
      isEditable: true,
      recipientId,
    };
  }
  if (!mergeValue) {
    return {
      value: '',
      isEditable: true,
    };
  }
  switch (mergeValue) {
    case EMergeValue.CompanyName:
      return sender ? senderCompanyName() : recipientCompanyName(recipientId);
    case EMergeValue.Fullname:
      return sender ? senderFullName() : recipientFullName(recipientId);
    case EMergeValue.SignDate:
      return sender ? senderSignDate() : recipientSignDate(recipientId);
  }
}

function senderCompanyName(): Partial<IField> {
  return {
    value: EMergeType.SenderCompanyName,
    isEditable: true,
  };
}

function senderFullName(): Partial<IField> {
  return {
    value: EMergeType.SenderName,
    isEditable: true,
  };
}

function senderSignDate(): Partial<IField> {
  return {
    value: EMergeType.SignDateStandard,
    isEditable: true,
  };
}

function recipientCompanyName(recipientId: string): Partial<IField> {
  return {
    value: EMergeType.RecipientCompanyName,
    isEditable: true,
    recipientId,
  };
}

function recipientFullName(recipientId: string): Partial<IField> {
  return {
    value: EMergeType.RecipientName,
    isEditable: true,
    recipientId,
  };
}

function recipientSignDate(recipientId: string): Partial<IField> {
  return {
    value: EMergeType.SignDateStandard,
    isEditable: true,
    recipientId,
  };
}

export function defaultSizes(type: EFieldType): { width: number; height: number } {
  switch (type) {
    case EFieldType.Initials:
      return {
        height: 39,
        width: 78,
      };
    case EFieldType.Signature:
      return {
        width: 146,
        height: 49,
      };
    case EFieldType.Check:
      return {
        height: 14,
        width: 14,
      };
    default:
      return {
        height: 24,
        width: 245,
      };
  }
}

function defaultPositionDroppedField(
  type: EFieldType,
  left: number,
  top: number,
  pageWidth: number
): IFieldPosition {
  const size: { width: number; height: number } = defaultSizes(type);
  const placement: { left: number; top: number } = getDroppedFieldPlacement(left, top, pageWidth);

  return {
    top: placement.top,
    left: placement.left,
    width: size.width,
    height: size.height,
  };
}

function getDroppedFieldPlacement(left, top, pageWidth): { top: number; left: number } {
  const placement: { top: number; left: number } = {
    top,
    left,
  };
  const ratio: number = FieldHelper.getPageRatio(pageWidth);
  placement.left = Math.round(placement.left / ratio);
  placement.top = Math.round(placement.top / ratio);

  return placement;
}
